import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { createOrder, getOrderDiagnostics } from '../services/api.trading';
import { getApiErrorMessage } from '../lib/apiErrors';

export const useOrderEntry = ({
  selectedTradingAccountId,
  activeAccountContractLink,
  symbol,
  displayedPaperMode,
  selectedTradingAccount,
  refreshOrderDiagnostics,
  onOrderPlaced,
}) => {
  const [orderType, setOrderType] = useState("MARKET");
  const [orderSide, setOrderSide] = useState("BUY");
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState("");
  const [stopLoss, setStopLoss] = useState("");
  const [takeProfit, setTakeProfit] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const placeOrder = useCallback(async () => {
    if (!selectedTradingAccountId) {
      toast.error("Please select a trading account");
      return;
    }

    setIsSubmitting(true);
    try {
      if (!displayedPaperMode) {
        let diagnostics = null;
        try {
          if (typeof refreshOrderDiagnostics === "function") {
            diagnostics = await refreshOrderDiagnostics(selectedTradingAccountId);
          } else {
            const diagnosticsResponse = await getOrderDiagnostics({
              trading_account_id: selectedTradingAccountId,
              symbol,
            });
            diagnostics = diagnosticsResponse?.data || null;
          }
        } catch (diagnosticsError) {
          console.warn("Order diagnostics refresh failed before submit:", diagnosticsError);
        }

        const preflight = diagnostics?.preflight || null;
        const quoteGuardrail = preflight?.quote_guardrail || null;
        if (preflight && preflight.ready_for_live_submit === false) {
          const guardrailMessage = quoteGuardrail && !quoteGuardrail.fresh
            ? `Live quote for ${symbol} is missing or stale. Refresh broker data before submitting a live order.`
            : null;
          toast.error(preflight.error || guardrailMessage || "Broker preflight blocked this live order");
          return null;
        }
      }

      const orderData = {
        trading_account_id: selectedTradingAccountId,
        account_strategy_id: activeAccountContractLink?.id || null,
        symbol,
        side: orderSide,
        quantity: parseInt(quantity),
        order_type: orderType,
        price: orderType === "LIMIT" ? parseFloat(price) : null,
        stop_loss: stopLoss ? parseFloat(stopLoss) : null,
        take_profit: takeProfit ? parseFloat(takeProfit) : null,
        broker_contract_id: activeAccountContractLink?.broker_contract_id || null,
        broker_contract_label: activeAccountContractLink?.broker_contract_label || null,
        paper_mode: displayedPaperMode,
      };

      const res = await createOrder(orderData);
      
      if (res.data?.bracket_notice) {
        toast.warning(res.data.bracket_notice);
      }
      
      const isLive = res.data?.execution_mode === "live";
      const accountName = selectedTradingAccount?.account_name ? ` on ${selectedTradingAccount.account_name}` : "";
      const strategyName = activeAccountContractLink?.strategy_name ? ` using ${activeAccountContractLink.strategy_name}` : "";
      
      toast.success(
        isLive
          ? `${orderSide} ${quantity} ${symbol} queued to Topstep${accountName}`
          : `${orderSide} paper order placed for ${quantity} ${symbol}${accountName}${strategyName}`
      );

      if (onOrderPlaced) {
        onOrderPlaced(res.data);
      }
      
      return res.data;
    } catch (error) {
      console.error("Error placing order:", error);
      toast.error(getApiErrorMessage(error, "Failed to place order"));
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedTradingAccountId,
    activeAccountContractLink,
    symbol,
    orderSide,
    quantity,
    orderType,
    price,
    stopLoss,
    takeProfit,
    displayedPaperMode,
    refreshOrderDiagnostics,
    selectedTradingAccount,
    onOrderPlaced
  ]);

  return {
    orderType, setOrderType,
    orderSide, setOrderSide,
    quantity, setQuantity,
    price, setPrice,
    stopLoss, setStopLoss,
    takeProfit, setTakeProfit,
    placeOrder,
    isSubmitting
  };
};
